import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Lock, User, ArrowRight, Loader2, ShieldAlert, Link2 } from 'lucide-react';
import { errorMessage } from '../lib/apiError';
import logo from '../assets/logo-stacked.png';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATS = [
  { k: 'Catalogue', v: '1,914', u: 'SKUs' },
  { k: 'Channel aliases', v: '5,105', u: 'mapped' },
  { k: 'Dispatch feed', v: '15', u: 'min sync' },
  { k: 'Warehouses', v: 'Multi', u: 'site' },
];

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [ssoAvailable, setSsoAvailable] = useState(false);
  const [params] = useSearchParams();
  const ssoFailed = params.get('sso') === 'failed';
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API_URL}/api/v1/auth/sso/status`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setSsoAvailable(!!d?.configured))
      .catch(() => setSsoAvailable(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/v1/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(errorMessage(error, 'Invalid credentials'));
      }

      const data = await response.json();
      login(data.token, data.user);
      navigate('/');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-canvas">
      {/* Console panel */}
      <div className="relative lg:w-[46%] bg-ink text-white overflow-hidden flex-shrink-0">
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(148,163,184,.35) 1px, transparent 0), linear-gradient(90deg, rgba(148,163,184,.35) 1px, transparent 0)',
            backgroundSize: '48px 48px',
          }}
        />
        <div className="absolute -right-24 top-1/3 w-[420px] h-[420px] rounded-full bg-sky-500/10 blur-[100px]" />

        <div className="relative z-10 flex flex-col justify-between h-full p-6 lg:p-12 min-h-[128px] lg:min-h-screen">
          <div className="flex items-center gap-3">
            <img src={logo} alt="SND" className="h-9 w-9 object-contain brightness-0 invert" />
            <div>
              <p className="text-sm font-semibold tracking-tight">StarAndDaisy</p>
              <p className="text-2xs uppercase tracking-[0.18em] text-slate-400">Inventory Operations</p>
            </div>
          </div>

          <div className="hidden lg:block py-10">
            <p className="text-2xs uppercase tracking-[0.2em] text-sky-400 mb-4">Operations Console</p>
            <h1 className="text-4xl xl:text-5xl font-semibold leading-[1.05] tracking-tight max-w-md text-white">
              Every unit,
              <span className="block text-slate-400">every channel,</span>
              <span className="block">one ledger.</span>
            </h1>
            <p className="mt-5 text-sm text-slate-400 max-w-sm leading-relaxed">
              Marketplace dispatches deduct stock automatically. Reorder signals come from real run
              rates, not guesses.
            </p>
          </div>

          <div className="hidden lg:grid grid-cols-2 gap-px bg-white/10 rounded-lg overflow-hidden">
            {STATS.map((s) => (
              <div key={s.k} className="bg-ink px-4 py-3.5">
                <p className="text-2xs uppercase tracking-[0.14em] text-slate-500">{s.k}</p>
                <p className="mt-1 flex items-baseline gap-1.5">
                  <span className="mono text-xl font-medium text-white">{s.v}</span>
                  <span className="text-2xs text-slate-500">{s.u}</span>
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-sm animate-slide-up">
          <p className="eyebrow">Sign in</p>
          <h2 className="text-2xl font-semibold text-slate-900 tracking-tight mt-1.5">
            Welcome back
          </h2>
          <p className="text-sm text-slate-500 mt-1.5">
            Use your inventory account to continue.
          </p>

          {ssoFailed && (
            <div
              className="mt-5 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 flex items-start gap-2.5"
              data-testid="sso-failed-banner"
            >
              <ShieldAlert className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-700 leading-relaxed">
                That Operations sign-in link did not work. Links last 60 seconds and work once — open
                the Inventory tile again from the Operations hub.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div>
              <label htmlFor="username" className="label">Username</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  id="username"
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="input-with-icon"
                  placeholder="your.username"
                  required
                  data-testid="username-input"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="label">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-with-icon"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-2.5"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>Sign in <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </form>

          {ssoAvailable && (
            <div className="mt-6 pt-5 border-t border-slate-200" data-testid="sso-available-note">
              <div className="flex items-start gap-2.5 rounded-lg bg-brand-soft border border-slate-200 px-3.5 py-3">
                <Link2 className="w-4 h-4 text-brand-soft-fg flex-shrink-0 mt-0.5" />
                <p className="text-xs text-brand-soft-fg leading-relaxed">
                  <span className="font-semibold">Staff:</span> open the Inventory tile from the
                  StarAndDaisy Operations hub and you will be signed in automatically — no password
                  needed.
                </p>
              </div>
            </div>
          )}

          <p className="mt-8 text-2xs uppercase tracking-[0.14em] text-slate-400">
            StarAndDaisy · Inventory Management System
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
